<template>
  <section
    v-if="!isLoading && nearestCities.length"
    class="around-city-wrap around-city-wrap__around--experience"
  >
    <div class="title-wrap">
      <div class="feature-title feature-title--selections">
        <div
          class="feature-title__link"
          data-test="listing-around-title"
          itemprop="item"
        >
          Достопримечательности в других городах
        </div>
      </div>
    </div>
    <div class="around-city-inner-wrap">
      <div class="around-city-inner">
        <div class="around-city">
          <template v-if="cities.length">
            <CitySnippet
              v-for="(city, index) in cities"
              :key="city.slug"
              :city="city"
              :is-nearest-city-excursions="isNearestCityExcursions"
              :data-test="`listing-around-item-${index}`"
            />
          </template>
          <template v-else>
            <SkeletonItem
              v-for="skeleton in 8"
              :key="skeleton"
              class="around-city-skeleton"
            />
          </template>
        </div>
      </div>
    </div>
    <div
      v-if="!hideShowMoreButton"
      class="btn-style"
    >
      <UiButton
        :variant="UiButtonVariant.OUTLINE"
        :color-mode="UiButtonColorMode.NEUTRAL"
        text="Показать ещё"
        data-test="listing-around-title"
        @clicked="showMoreCities"
      />
    </div>
  </section>
</template>

<script setup lang="ts">
  import { UiButton, UiButtonVariant, UiButtonColorMode } from '@experience/core-button-ui';
  import { SkeletonItem } from '@experience/core-skeleton-lib';
  import { ref, computed, onMounted, toRaw } from 'vue';
  import { useRoute } from 'vue-router';

  import { trackEvent } from '@/services/analytics';
  import { getCityAround, type CityAround } from '@/shared/api/generated/api';
  import { useSeoStore } from '@/shared/store/seo';
  import CitySnippet from '@/shared/ui/CitySnippet.vue';
  import { useScreenSize } from '@/shared/utils/breakpoints';

  const props = defineProps<{
    isNearestCityExcursions: boolean;
    /**
     * Как в монолите `around-city-block`: города с родителя (после bootstrap).
     * Без флага `useAroundFromParent` — запрос `getCityAround` внутри компонента.
     */
    aroundCities?: CityAround[];
    useAroundFromParent?: boolean;
  }>();

  const breakpoints = useScreenSize();

  const route = useRoute();
  const isLoading = ref(true);

  const nearestCitiesInternal = ref<CityAround[]>([]);

  const nearestCities = computed(() =>
    props.useAroundFromParent && props.aroundCities !== undefined ? props.aroundCities : nearestCitiesInternal.value,
  );

  const { slug } = route.params;
  const seoStore = useSeoStore();

  onMounted(async () => {
    if (props.useAroundFromParent) {
      isLoading.value = false;

      return;
    }

    if (!slug) {
      isLoading.value = false;

      return;
    }

    try {
      const { data } = await getCityAround({ path: { city: typeof slug === 'string' ? slug : (slug[0] ?? '') } });

      nearestCitiesInternal.value = data ?? [];
    } catch {
      nearestCitiesInternal.value = [];
    } finally {
      isLoading.value = false;
    }
  });

  const offset = ref<number>(0);
  const showNumber = ref<number>(10);

  const cities = computed<CityAround[]>(() => {
    if (breakpoints.isMobile.value || breakpoints.isTablet.value) {
      return nearestCities.value;
    }

    return nearestCities.value.slice(0, showNumber.value + offset.value);
  });

  const hideShowMoreButton = computed<boolean>(() => {
    return (
      nearestCities.value.length === cities.value.length || breakpoints.isMobile.value || breakpoints.isTablet.value
    );
  });

  const showMoreCities = (): void => {
    offset.value += showNumber.value;
    void trackEvent('attractions:other-cities:show-more-button.click', toRaw(seoStore.geoRequired));
  };
</script>

<style lang="scss" scoped>
  @use '@experience/shared/dist/assets/adaptivity' as legacy-adaptivity;
  @use '@/shared/styles/global/sights-common';

  .title-wrap {
    min-width: 950px;
    max-width: 1260px;
    padding: 0 20px;
    margin: 0 auto;

    @include legacy-adaptivity.tablet {
      box-sizing: border-box;
      width: 100%;
      min-width: 100%;
      padding: 0 20px;
    }
  }

  .feature-title.feature-title--selections {
    display: block;
    margin: 0 0 24px;

    @include sights-common.section-title;
  }

  .feature-title__link {
    color: var(--alias-color-link-darked);
    text-decoration: none;
  }

  .around-city-wrap {
    margin-top: 80px;

    .feature-title.feature-title--selections {
      display: block;
    }

    .arrow {
      position: relative;

      .icon {
        position: absolute;
        top: 55%;
        left: 4px;
        width: 20px;
        height: 20px;
        fill: var(--alias-color-icon-default);
        transform: translateY(-50%);
      }
    }

    @include legacy-adaptivity.tablet {
      margin-top: var(--alias-space-56);
    }
  }

  .around-city-inner-wrap {
    min-width: 950px;
    max-width: 1260px;
    padding: 0 20px;
    margin: 0 auto;

    @include legacy-adaptivity.tablet {
      box-sizing: border-box;
      width: 100%;
      min-width: 100%;
      padding: 0;
    }
  }

  .around-city-wrap__around {
    margin: 24px 0 48px;

    @include legacy-adaptivity.tablet {
      margin: 16px 0 40px;
    }
  }

  .around-city-wrap.around-city-wrap__around--experience .around-city {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    grid-auto-rows: 1fr;
    gap: 16px;

    @include legacy-adaptivity.tablet {
      grid-template-columns: repeat(4, 1fr);
      grid-auto-rows: auto;
      gap: 8px;
    }

    @include legacy-adaptivity.mobile {
      grid-template-rows: repeat(2, 1fr);
      grid-template-columns: auto;
      grid-auto-flow: column;
      gap: 8px;
      width: max-content;
      padding-right: 20px;
      overflow-x: auto;
      scrollbar-width: none;
      -ms-overflow-style: none;

      &::-webkit-scrollbar {
        display: none;
      }
    }
  }

  .around-city-wrap.around-city-wrap__around {
    .around-city-inner {
      .around-city {
        display: grid;
        grid-template-columns: repeat(5, 1fr);
        grid-auto-rows: 1fr;
        gap: 16px;

        &.one-row {
          @include legacy-adaptivity.tablet {
            grid-template-columns: repeat(4, 1fr);
            gap: 8px;
          }

          @include legacy-adaptivity.tablet {
            grid-template: 1fr / 176px;
            grid-auto-columns: 176px;
            grid-auto-flow: row;
            gap: 8px;
          }
        }

        @include legacy-adaptivity.tablet {
          grid-template-columns: repeat(2, 1fr);
        }
      }

      @include legacy-adaptivity.tablet {
        padding: 0 20px;
        overflow-x: scroll;
      }
    }
  }

  .around-city-inner {
    @include legacy-adaptivity.tablet {
      padding: 0 20px;

      &::-webkit-scrollbar {
        display: none;
      }
    }

    @include legacy-adaptivity.tablet {
      padding: 0 20px;
      overflow-x: scroll;
    }

    @include legacy-adaptivity.mobile {
      padding: 0 0 0 20px;
    }
  }

  .btn-style {
    margin-top: 24px;

    @include sights-common.centered-btn;

    @include legacy-adaptivity.tablet {
      box-sizing: border-box;
      padding: 0 20px;
      margin-top: 16px;
    }
  }

  .around-city-skeleton {
    position: relative;
    display: block;
    height: 174px;
    border-radius: var(--alias-radius-8);

    @include legacy-adaptivity.mobile {
      height: 121px;
    }
  }
</style>
